import {
  ArchiveOutlined,
  EditOutlined,
  FolderSpecialOutlined,
  MoreHoriz,
  MoreVert,
  SettingsOutlined,
  UnarchiveOutlined,
} from '@mui/icons-material';
import { IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getFolder, getFolders } from '../../../redux/actions/folderActions';
import { updateRelation } from '../../../redux/actions/userSpecificRelationsActions';
import { selectFolders, setCurrentFolder } from '../../../redux/reducers/folderReducer';
import { AppDispatch } from '../../../redux/store';
import { archiveProject } from '../../../services/dealGPT';
import { IconContainer, IconLabel } from '../../../theme/CustomComponents';
import { Folder } from '../../../Types/folder';
import { AnalyticsEvent, useAnalytics } from '../../Providers/AnalyticsProvider';
import { useAuth } from '../../Providers/AuthProvider';
import { useProjects } from '../../Providers/ProjectsProvider';

const IntellioAdvantageFolderOptionsMenu = ({
  folder,
  horizontalOptions = false,
}: {
  folder: Folder;
  horizontalOptions?: boolean;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const { isAdmin, isDealGPTAdmin } = useAuth();
  const { setShouldFetch } = useProjects();

  const { currentFolder } = useSelector(selectFolders);

  const [anchorFolderContextMenu, setAnchorFolderContextMenu] = useState<null | HTMLElement>(null);

  const handlePinFolderToggle = useCallback(
    (newValue: boolean) => {
      analytics.event(
        AnalyticsEvent.WM_NIGEL_FOLDER_PINNED,
        {
          id: folder.id,
        },
        {
          folderId: folder.id,
          fileWasPinned: !newValue,
          fileNowPinned: newValue,
          projectId: folder.projectId,
        }
      );
      dispatch(updateRelation({ folderId: folder.id, pinned: newValue })).then(async () => {
        await dispatch(getFolders({ foldersWithProjects: true }));

        if (currentFolder?.id === folder.id) {
          await dispatch(getFolder({ folderId: folder.id, isAdmin }));
        }

        setAnchorFolderContextMenu(null);
        setShouldFetch(true);
      });
    },
    [folder, dispatch, currentFolder?.id, analytics, isAdmin, setShouldFetch]
  );

  const handleEditProjectClick = useCallback(async () => {
    analytics.event(
      AnalyticsEvent.WM_NIGEL_FOLDER_EDIT,
      {
        id: folder.id,
      },
      { folderId: folder.id, projectId: folder.projectId }
    );
    if (currentFolder?.id !== folder.id) {
      dispatch(setCurrentFolder(folder));
    }
    setAnchorFolderContextMenu(null);
    navigate(`/folder/${folder.id}/edit`);
  }, [currentFolder, folder, navigate, dispatch, analytics]);

  const handleConfigureProjectClick = useCallback(async () => {
    analytics.event(
      AnalyticsEvent.WM_IA_P_CONFIG,
      {
        id: folder.id,
      },
      { folderId: folder.id, projectId: folder.projectId }
    );
    if (currentFolder?.id !== folder.id) {
      dispatch(setCurrentFolder(folder));
    }
    setAnchorFolderContextMenu(null);
    navigate(`/folder/${folder.id}/configure`);
  }, [currentFolder, folder, navigate, dispatch, analytics]);

  const handleArchiveClick = useCallback(
    async (status: boolean, id: string) => {
      const shouldArchive = !status;
      analytics.event(
        AnalyticsEvent.WM_IA_HOME_ARCHIVE,
        {
          id: id,
          value: `IsArchived=${shouldArchive}`,
        },
        {
          folderId: folder.id,
          projectId: id,
          oldIsArchived: !shouldArchive,
          newIsArchived: shouldArchive,
        }
      );

      try {
        const archiveResponse = await archiveProject(id, { archive: shouldArchive });

        if (archiveResponse) {
          const message = shouldArchive ? 'archived' : 'unarchived';
          enqueueSnackbar(`Project ${message} successfully`, { variant: 'success' });
          setShouldFetch(true);

          await dispatch(getFolders({ foldersWithProjects: true }));

          if (currentFolder?.id === folder.id) {
            dispatch(setCurrentFolder(undefined));
          }

          setAnchorFolderContextMenu(null);
        }
      } catch (error) {
        enqueueSnackbar(`${error}`, { variant: 'error' });
      }
    },
    [analytics, dispatch, folder.id, currentFolder?.id, setShouldFetch]
  );

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Tooltip
        id={`open-folder-card-settings-${folder.id}`}
        title={<Typography variant="caption"> Folder settings </Typography>}
        PopperProps={{ keepMounted: true }}
      >
        <IconButton
          onClick={(event) => {
            event.stopPropagation();
            setAnchorFolderContextMenu(event.currentTarget);
          }}
          aria-labelledby={`open-folder-card-settings-${folder.id}`}
          sx={{ p: 0 }}
        >
          {horizontalOptions ? (
            <MoreHoriz sx={{ color: '#070154' }} />
          ) : (
            <MoreVert sx={{ color: '#070154' }} />
          )}
        </IconButton>
      </Tooltip>

      <Menu
        sx={{ mt: '16px' }}
        anchorEl={anchorFolderContextMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorFolderContextMenu)}
        onClose={() => setAnchorFolderContextMenu(null)}
      >
        <MenuItem
          onClick={() => {
            handlePinFolderToggle(!folder.pinned);
          }}
        >
          <IconLabel>
            <FolderSpecialOutlined
              sx={folder.pinned ? { color: '#50658E' } : { color: '#F900D3' }}
            />
            {folder.pinned ? 'Unpin Folder' : 'Pin Folder'}
          </IconLabel>
        </MenuItem>
        {isDealGPTAdmin && (
          <MenuItem onClick={handleEditProjectClick}>
            <IconLabel>
              <EditOutlined />
              Edit Project
            </IconLabel>
          </MenuItem>
        )}
        <MenuItem onClick={handleConfigureProjectClick}>
          <IconLabel>
            <SettingsOutlined />
            Configure Project
          </IconLabel>
        </MenuItem>
        <MenuItem
          onClick={() =>
            handleArchiveClick(folder.project?.isArchived ?? false, folder.project?.id ?? '')
          }
        >
          <Typography textAlign="center">
            <IconContainer>
              {folder.project?.isArchived ? (
                <>
                  <UnarchiveOutlined />
                  <span>Unarchive</span>
                </>
              ) : (
                <>
                  <ArchiveOutlined />
                  <span>Archive</span>
                </>
              )}
            </IconContainer>
          </Typography>
        </MenuItem>
      </Menu>
    </div>
  );
};

export default IntellioAdvantageFolderOptionsMenu;
